import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity, type EntityData } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';

@Entity()
export class Message extends AdornisEntity {
  static override _class = 'Message';

  constructor(doc: Omit<EntityData<Message>, '_id' | '_class'> & { _id?: string; _class?: string }) {
    if (!doc._id) {
      doc._id = A.getGloballyUniqueID();
    }
    super(doc);
  }

  @Field(() => String)
  _id: Maybe<string>;

  @Field(() => String)
  content: Maybe<string | any>;
}

@Entity()
export class UserMessage extends Message {
  static override _class = 'UserMessage';
}

@Entity()
export class AssistantMessage extends Message {
  static override _class = 'AssistantMessage';
}

@Entity()
export class SystemInfoMessage extends Message {
  static override _class = 'SystemInfoMessage';
}

@Entity()
export class ConfirmationMessage extends Message {
  static override _class = 'ConfirmationMessage';

  @Field(() => Boolean)
  confirmed: Maybe<boolean>;

  @Field(() => String)
  confirmedBy: Maybe<string>;

  @Field(() => String)
  confirmationType: Maybe<string>;

  constructor(doc: Omit<EntityData<ConfirmationMessage>, '_id' | '_class'> & { _id?: string; _class?: string }) {
    const normalizedDoc = {
      ...doc,
      confirmed: doc.confirmed ?? false,
      confirmedBy: doc.confirmedBy ?? 'pending',
      confirmationType: doc.confirmationType ?? 'structure',
    } as any;
    super(normalizedDoc);
  }
}
@Entity()
export class ToolCall extends Message {
  static override _class = 'ToolCall';

  @Field(() => String)
  toolName: Maybe<string>;

  @Field(() => String)
  toolDescription: Maybe<string>;
}

@Entity()
export class AssignmentProposalMessage extends Message {
  static override _class = 'AssignmentProposalMessage';

  @Field(() => String)
  baseContent: Maybe<string>;

  @Field(() => [String])
  schuelerabgaben: Maybe<string[]>;

  @Field(() => String)
  musterloesung: Maybe<string>;

  @Field(() => String)
  bewertungsschema: Maybe<string>;

  @Field(() => String)
  leereKlausur: Maybe<string>;

  /**
   * Whether the "Leere Klausur" (blank exam) category applies to this proposal.
   * Fixed at creation time: blank exams can only be uploaded during the first upload,
   * so this is `false` for re-upload proposals. The UI uses it to decide whether to
   * render the blank-exam slot — independent of the (mutable) `leereKlausur` value,
   * so it survives the panel's persist round-trip.
   */
  @Field(() => Boolean)
  allowBlankExam: Maybe<boolean>;

  @Field(() => Boolean)
  active: Maybe<boolean>;

  constructor(doc: Omit<EntityData<AssignmentProposalMessage>, '_id' | '_class'> & { _id?: string; _class?: string }) {
    const normalizedDoc = {
      ...doc,
      active: doc.active ?? true,
      // Default to true so a proposal without an explicit flag shows the slot (first-upload behaviour).
      allowBlankExam: doc.allowBlankExam ?? true,
      schuelerabgaben: doc.schuelerabgaben ?? [],
      baseContent: doc.baseContent ?? doc.content ?? '',
    } as any;
    super(normalizedDoc);
  }

  static buildAgentContentForPlainObject(m: any): string {
    try {
      if (!m) return '';
      const base = String(m.baseContent ?? m.content ?? '').trim();
      const meta: any = {
        schuelerabgaben: Array.isArray(m.schuelerabgaben) ? m.schuelerabgaben : [],
        musterloesung: typeof m.musterloesung === 'string' ? m.musterloesung : null,
        bewertungsschema: typeof m.bewertungsschema === 'string' ? m.bewertungsschema : null,
        leereKlausur: typeof m.leereKlausur === 'string' ? m.leereKlausur : null,
        active: typeof m.active === 'boolean' ? m.active : true,
        baseContent: base,
        _id: m._id,
      };
      const metaStr = JSON.stringify(meta);
      return `${base}\n[[ASSIGNMENT_PROPOSAL_META:${metaStr}]]`;
    } catch (err) {
      return String(m.content ?? '');
    }
  }
}

// NOTE: buildAgentContent is now an instance method on AssignmentProposalMessage.
// For plain DB objects, callers should construct an instance if they need the
// instance method; otherwise fall back to using `content` directly.

@Entity()
export class Conversation extends MongoEntity {
  static override _class = 'Conversation';
  static override _collectionName = 'conversations';

  @Field(() => Boolean)
  thinking: Maybe<boolean>;

  @Field(() => String)
  language: Maybe<string>;

  @Field(() => String)
  examId: Maybe<string>;

  // whenever message array is updated, lastUpdated should be set to current time
  @Field(() => [Message])
  messages: Maybe<Message[]>;

  @Field(() => [Suggestion])
  suggestions: Maybe<Suggestion[]>;

  @Field(() => Boolean)
  suggestionsDirty: Maybe<boolean>;

  @Field(() => String)
  pendingCanvasOpen: Maybe<string>;

  @Field(() => String)
  pendingDownload: Maybe<string>;

  /** JSON `{ language?, theme? }` set by the change_settings tool; applied and cleared by the client. */
  @Field(() => String)
  pendingSettingsChange: Maybe<string>;

  /** ID of the user who owns this conversation */
  @Field(() => String)
  userID: Maybe<string>;

  /**
   * Server-set flag: true when an agent/system message has been added that the user
   * hasn't viewed yet. Flipped by the global change-stream watcher
   * (server/conversation-unread-watcher.ts) and cleared by `markConversationAsReadMutation`.
   */
  @Field(() => Boolean)
  hasUnreadReply: Maybe<boolean>;

  // set to current date whenever messages are updated; a new conversation starts with its creation time
  @Field(() => Date, { default: v => v ?? new Date() })
  lastUpdated!: Date;
}

@Entity()
export class Suggestion extends AdornisEntity {
  static override _class = 'Suggestion';

  @Field(() => String)
  _id: Maybe<string>;

  @Field(() => String)
  label: Maybe<string>;

  @Field(() => String)
  prompt: Maybe<string>;

  @Field(() => Boolean)
  userInputRequired?: boolean;

  @Field(() => Boolean)
  isUpload?: boolean;

  @Field(() => String)
  icon?: Maybe<string>;

  constructor(doc: Omit<EntityData<Suggestion>, '_id' | '_class'> & { _id?: string; _class?: string }) {
    if (!doc._id) {
      doc._id = A.getGloballyUniqueID();
    }
    super({
      ...doc,
      isUpload: doc.isUpload ?? false,
    });
  }
}
